'use client'

import { cn } from '@brew/ui/cn'
import {
  PromptInput,
  PromptInputActionMenuMobile,
  type PromptInputAttachmentsState,
  PromptInputDictationButton,
  PromptInputFooter,
  type PromptInputMentionItem,
  type PromptInputMessage,
  PromptInputTextarea,
  PromptInputTools,
} from '@/components/ai-elements/prompt-input'
import { getComposerBrandGatePlaceholder } from '@/components/chat/composer-brand-gate'
import { useOptionalBrandContext } from '@/contexts/brand-context'
import { usePromptSuggestionContext } from '@/hooks/use-prompt-suggestion-context'
import type { AutocompleteChatTrail } from './autocomplete-chat-trail'
import { ChatHandoffButton } from './chat-handoff-button'
import { ComposerAttachments, ComposerSubmitButton } from './composer'

export function ChatPromptInputBar({
  boxOnly,
  fillWidth = false,
  handleFetchChatMentions,
  initialOrgId: _initialOrgId,
  input,
  isLoading,
  isOverCreditLimit = false,
  enableAutocomplete = true,
  enableDictation = false,
  autocompleteSurface = 'chat',
  getAutocompleteChatTrail,
  onChangeInput,
  onStop,
  onSubmit,
  resume,
  status,
  selectedEmailChips,
  focusRequestKey,
  hasImplicitContent = false,
  chatId,
  placeholder: placeholderOverride,
  inputLabel,
  attachmentsState,
}: {
  boxOnly: boolean
  /**
   * When true, the composer fills the available column width instead of
   * being centered with `max-w-3xl` (matching the thread column). The
   * canvas chat panel passes this so
   * the composer follows the user-resized panel rather than capping to a
   * fixed width inside an already-narrow column.
   */
  fillWidth?: boolean
  handleFetchChatMentions: (
    query: string
  ) => Promise<Array<PromptInputMentionItem>>
  initialOrgId: string
  input: string
  isLoading: boolean
  /** When true, disable the submit affordance — the credits gate hook will pop a dialog on attempted submit. */
  isOverCreditLimit?: boolean
  /**
   * Forwarded to `PromptInputTextarea`. Set to false on unauthenticated
   * surfaces so the public composer never invokes
   * `/api/prompt-autocomplete`.
   */
  enableAutocomplete?: boolean
  /**
   * Show the microphone (speech-to-text) toggle beside the add button.
   * Signed-in surfaces only: it mints a Gateway token server-side.
   */
  enableDictation?: boolean
  /** Which composer surface this bar serves — labels autocomplete context. */
  autocompleteSurface?: 'chat' | 'canvas-chat'
  /**
   * Lazily builds the conversation trail for autocomplete context at
   * fetch time. Callers that own `messages` pass a ref-backed builder
   * (see `buildAutocompleteChatTrail`).
   */
  getAutocompleteChatTrail?: () => AutocompleteChatTrail | null
  onChangeInput: (value: string) => void
  /**
   * Stop the in-flight response. When omitted the submit button still
   * shows the streaming state but the ■ is inert — so any surface that
   * can stream MUST pass this (see `useConvexChatRuntime.stopStream`).
   */
  onStop?: () => void
  /** Resolve `false` when the send is declined (see `PromptInput`). */
  onSubmit: (
    message: PromptInputMessage,
    event: React.FormEvent<HTMLFormElement>
  ) => Promise<unknown>
  resume: boolean
  status: 'submitted' | 'streaming' | 'ready' | 'error'
  selectedEmailChips?: React.ReactNode
  /**
   * Bump to focus the textarea (e.g. after picking an in-thread
   * follow-up that filled the composer).
   */
  focusRequestKey?: number
  /**
   * Treat the composer as having content even if the textarea is empty and
   * no files are attached. Used by the templates remix flow where the
   * source-email chip is the implicit task.
   */
  hasImplicitContent?: boolean
  /**
   * When set (a real, PERSISTED chat — callers gate on live messages), shows the
   * "Open in <agent>" hand-off button left of submit — carry the whole
   * conversation to an external agent.
   */
  chatId?: string
  /**
   * Surface-specific placeholder (a remix box says what it remixes). Only
   * replaces the default copy — the brand-gate placeholders still win while
   * the active brand is not ready.
   */
  placeholder?: string
  inputLabel?: string
  /**
   * Uploaded files owned by the caller instead of the composer — the global
   * chat keeps them per chat so they survive remounts and move with "New
   * chat" (`components/canvas/chat-composer-uploads.ts`).
   */
  attachmentsState?: PromptInputAttachmentsState
}) {
  // Centralized brand gate: every consumer of ChatPromptInputBar inherits
  // the rule that submit is blocked until the ACTIVE brand is fully
  // extracted — an in-flight brand is a real, selectable brand everywhere
  // else in the app. The orchestrator route also enforces this server-side,
  // but disabling here gives users an obvious affordance and a clear
  // placeholder shift. A missing context (public surfaces) never gates.
  const brandCtx = useOptionalBrandContext()
  const isActiveBrandReady = brandCtx ? brandCtx.isActiveBrandReady : true
  const gatePlaceholder = getComposerBrandGatePlaceholder({
    isActiveBrandReady,
    hasReadyBrand: brandCtx ? brandCtx.hasReadyBrand : true,
  })
  const placeholder =
    isActiveBrandReady && placeholderOverride
      ? placeholderOverride
      : gatePlaceholder
  const suggestionContext = usePromptSuggestionContext({
    surface: autocompleteSurface,
    getChatTrail: getAutocompleteChatTrail,
  })
  return (
    <PromptInput
      attachmentsState={attachmentsState}
      onSubmit={onSubmit}
      className={cn('mt-0', { 'mx-auto max-w-3xl': !fillWidth })}
      data-testid="prompt-input"
    >
      {selectedEmailChips}
      <ComposerAttachments />
      <PromptInputTextarea
        aria-label={inputLabel}
        shimmerPlaceholder={boxOnly}
        disabled={(!boxOnly && isLoading) || !isActiveBrandReady}
        enableAutocomplete={enableAutocomplete}
        focusRequestKey={focusRequestKey}
        getSuggestionContext={suggestionContext.getContext}
        onChange={(event) => onChangeInput(event.currentTarget.value)}
        onFetchMentions={handleFetchChatMentions}
        placeholder={placeholder}
        value={input}
        data-testid="prompt-textarea"
      />
      <PromptInputFooter>
        <PromptInputTools>
          <PromptInputActionMenuMobile />
          {enableDictation ? <PromptInputDictationButton /> : null}
        </PromptInputTools>
        <div className="flex items-center gap-2">
          {!boxOnly && chatId ? (
            <ChatHandoffButton
              chatId={chatId}
              brandName={brandCtx?.brand?.brandName}
              status={status}
            />
          ) : null}
          {boxOnly ? (
            <ComposerSubmitButton
              disabled={isLoading || isOverCreditLimit || !isActiveBrandReady}
              input={input}
              status="ready"
              hasImplicitContent={hasImplicitContent}
            />
          ) : (
            <ComposerSubmitButton
              disabled={
                // 'submitted' stays clickable when a stop handler
                // exists: the pre-first-token window is exactly when
                // people change their mind, and the runtime holds the
                // request until the run registers its lease.
                (status === 'submitted' && !onStop) ||
                isLoading ||
                isOverCreditLimit ||
                !isActiveBrandReady
              }
              input={input}
              allowStop={!resume}
              onStop={onStop}
              status={status}
              hasImplicitContent={hasImplicitContent}
            />
          )}
        </div>
      </PromptInputFooter>
    </PromptInput>
  )
}
